import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query";
import {
	createRulesLibraryList,
	CreateRulesLibraryListRequest,
	deleteRulesLibraryList,
	getRulesLibrary,
	getRulesLibrarySuggestions,
	GetRulesLibraryResponse,
	GetRulesLibrarySuggestionsResponse,
	promoteInClassRuleToList,
	PromoteInClassRuleToListRequest,
	RulesLibraryList,
	updateRulesLibraryList,
	UpdateRulesLibraryListRequest,
	validateRulesLibraryRule,
	ValidateRuleRequest,
} from "@/api/rulesLibrary";
import { sortRulesLibraryLists } from "@/components/RulesLibraryDrawer/utils";
import { classroomRulesQueryRootKey } from "./classroomRulesQuery";

const rulesLibraryQueryKey = ["rulesLibrary"] as const;
const rulesLibrarySuggestionsQueryKey = ["rulesLibrarySuggestions"] as const;

export function useRulesLibraryQuery(applianceId: string): UseQueryResult<GetRulesLibraryResponse> {
	return useQuery<GetRulesLibraryResponse, Error>({
		queryKey: rulesLibraryQueryKey,
		queryFn: () => getRulesLibrary(applianceId),
		enabled: Boolean(applianceId),
		retry: false,
	});
}

export function useRulesLibrarySuggestionsQuery(applianceId: string): UseQueryResult<GetRulesLibrarySuggestionsResponse> {
	return useQuery<GetRulesLibrarySuggestionsResponse, Error>({
		queryKey: rulesLibrarySuggestionsQueryKey,
		queryFn: () => getRulesLibrarySuggestions(applianceId),
		enabled: Boolean(applianceId),
		retry: false,
	});
}

export function useCreateRulesLibraryListMutation(
	applianceId: string
): UseMutationResult<RulesLibraryList, Error, CreateRulesLibraryListRequest> {
	const queryClient = useQueryClient();

	return useMutation<RulesLibraryList, Error, CreateRulesLibraryListRequest>({
		mutationFn: (body) => createRulesLibraryList(applianceId, body),
		onSuccess: (createdList) => {
			queryClient.setQueryData<GetRulesLibraryResponse>(rulesLibraryQueryKey, (previous) => ({
				classroomRulesLists: sortRulesLibraryLists([...(previous?.classroomRulesLists ?? []), createdList]),
			}));
		},
	});
}

export type UpdateRulesLibraryListVariables = {
	listId: string;
	body: UpdateRulesLibraryListRequest;
};

export type PromoteInClassRuleToListVariables = {
	listId: string;
	body: PromoteInClassRuleToListRequest;
};

export function useUpdateRulesLibraryListMutation(
	applianceId: string
): UseMutationResult<RulesLibraryList, Error, UpdateRulesLibraryListVariables> {
	const queryClient = useQueryClient();

	return useMutation<RulesLibraryList, Error, UpdateRulesLibraryListVariables>({
		mutationFn: ({ listId, body }) => updateRulesLibraryList(applianceId, listId, body),
		onSuccess: (updatedList) => {
			queryClient.setQueryData<GetRulesLibraryResponse>(rulesLibraryQueryKey, (previous) => {
				if (!previous) {
					return previous;
				}

				return {
					classroomRulesLists: previous.classroomRulesLists.map((list) => (list.id === updatedList.id ? updatedList : list)),
				};
			});
			// Applied lists on Active Rules are a classroom-rules read, not the library cache.
			void queryClient.invalidateQueries({ queryKey: classroomRulesQueryRootKey });
		},
	});
}

export function usePromoteInClassRuleToListMutation(
	applianceId: string
): UseMutationResult<RulesLibraryList, Error, PromoteInClassRuleToListVariables> {
	const queryClient = useQueryClient();

	return useMutation<RulesLibraryList, Error, PromoteInClassRuleToListVariables>({
		mutationFn: ({ listId, body }) => promoteInClassRuleToList(applianceId, listId, body),
		onSuccess: (updatedList) => {
			queryClient.setQueryData<GetRulesLibraryResponse>(rulesLibraryQueryKey, (previous) => {
				if (!previous) {
					return previous;
				}

				return {
					classroomRulesLists: previous.classroomRulesLists.map((list) => (list.id === updatedList.id ? updatedList : list)),
				};
			});
			// The promoted rule leaves in-class rules and appears on the applied list.
			void queryClient.invalidateQueries({ queryKey: classroomRulesQueryRootKey });
		},
	});
}

export function useValidateRulesLibraryRuleMutation(applianceId: string): UseMutationResult<void, Error, ValidateRuleRequest> {
	return useMutation<void, Error, ValidateRuleRequest>({
		mutationFn: (body) => validateRulesLibraryRule(applianceId, body),
	});
}

export function useDeleteRulesLibraryListMutation(applianceId: string): UseMutationResult<void, Error, string> {
	const queryClient = useQueryClient();

	return useMutation<void, Error, string>({
		mutationFn: (listId) => deleteRulesLibraryList(applianceId, listId),
		onSuccess: (_data, listId) => {
			queryClient.setQueryData<GetRulesLibraryResponse>(rulesLibraryQueryKey, (previous) => {
				if (!previous) {
					return previous;
				}

				return {
					classroomRulesLists: previous.classroomRulesLists.filter((list) => list.id !== listId),
				};
			});
			void queryClient.invalidateQueries({ queryKey: classroomRulesQueryRootKey });
		},
	});
}
